import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { env } from "../env"

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

export const rotateImage = (imageBase64: string, rotation: number, cb: (arg0: string) => void) => {
	const img = new Image()
	img.src = imageBase64
	img.onload = () => {
		const canvas = document.createElement("canvas")

		canvas.width = img.height
		canvas.height = img.width
		const ctx = canvas.getContext("2d")

		ctx?.translate(Math.floor(img.height / 2), Math.floor(img.width / 2))

		ctx?.rotate(rotation * (Math.PI / 180))

		ctx?.drawImage(img, -img.width / 2, -img.height / 2)

		cb(canvas.toDataURL("image/jpeg"))
	}
}

export function getNestedValue(obj: any, keyPath: string): any {
	return keyPath.split(".").reduce((acc, key) => {
		return acc?.[key]
	}, obj)
}

export function normalizeString(str: string | null): string {
	if (!str) return ""
	return str
		.toString()
		.toLowerCase()
		.replace(/[^\w\s]/g, "")
		.replace(/\s+/g, "")
		.trim()
}

export function areValuesEquivalent(val1: string | null, val2: string | null): boolean {
	if (val1 === null && val2 === null) return true
	if (val1 === null || val2 === null) return false

	return normalizeString(val1) === normalizeString(val2)
}

export const getErrorMessage = (error: unknown): string => {
	if (typeof error === "object" && error !== null) {
		if ("message" in error && typeof error.message === "string") {
			return error.message
		}
	}
	return "Ocorreu um erro desconhecido. Por favor, tente novamente."
}

export function pageTitle(title: string, productName?: string): string {
	const baseTitle = productName || "Sistema de Gestão"
	return title ? `${title} | ${baseTitle}` : baseTitle
}

export const formatCPF = (value: string) => {
	const numbers = value.replace(/\D/g, "")
	return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
}

export function formatDate(date: Date | string): string {
	const dateObj = typeof date === "string" ? new Date(date) : date
	return dateObj.toLocaleDateString("pt-BR", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	})
}

export function formatDateTime(date: Date | string): string {
	const dateObj = typeof date === "string" ? new Date(date) : date
	return dateObj.toLocaleString("pt-BR", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	})
}

export function generateId(): string {
	return Math.random().toString(36).substring(2) + Date.now().toString(36)
}

export function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
	let timeout: NodeJS.Timeout
	return (...args: Parameters<T>) => {
		clearTimeout(timeout)
		timeout = setTimeout(() => func(...args), wait)
	}
}

export const getCoordinates = (coords: GeolocationCoordinates | undefined) => {
	const devLat = env.VITE_PUBLIC_DEV_LATITUDE
	const devLng = env.VITE_PUBLIC_DEV_LONGITUDE
	const isDev = env.MODE === "development"

	if (isDev && devLat && devLng)
		return {
			latitude: Number.parseFloat(devLat),
			longitude: Number.parseFloat(devLng),
		}

	if (
		coords &&
		typeof coords === "object" &&
		typeof coords.latitude === "number" &&
		typeof coords.longitude === "number" &&
		coords.latitude !== null &&
		coords.longitude !== null &&
		coords.latitude !== 0 &&
		coords.longitude !== 0 &&
		!Number.isNaN(coords.latitude) &&
		!Number.isNaN(coords.longitude)
	)
		return coords

	return null
}
